import { useEffect, useState } from 'react'
import { lumi } from '@/lib/lumi'
import {
  optionalTokenSocials,
  pickTokenSocials,
  trimOptionalUrl,
  type TokenSocials,
} from '@/lib/tokenSocials'

export type ProjectLinkKind = 'curve' | 'presale'

export type ProjectLinkRecord = {
  website?: string
  image?: string
  banner?: string
  about?: string
  socials?: TokenSocials
}

export const MAX_ABOUT_LENGTH = 2000

export function sanitizeAbout(value?: string) {
  const trimmed = value?.replace(/\r\n/g, '\n').replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '').trim()
  if (!trimmed)
    return undefined
  return trimmed.slice(0, MAX_ABOUT_LENGTH)
}

export function trimOptionalAbout(value?: string) {
  const trimmed = value?.replace(/\r\n/g, '\n').replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '').trim()
  if (!trimmed)
    return undefined
  if (trimmed.length > MAX_ABOUT_LENGTH)
    throw new Error(`About must be ${MAX_ABOUT_LENGTH} characters or fewer`)
  return trimmed
}

const ADDRESS_RE = /^0x[a-fA-F0-9]{40}$/
const AUTH_PREFIX = 'Penguin Pad: authorize project link updates'
const AUTH_TTL_MS = 24 * 60 * 60 * 1000

type LinkAuth = {
  wallet: string
  message: string
  signature: string
  expiresAt: number
}

let cachedAuth: LinkAuth | null = null
const LINKS_SAVED_EVENT = 'penguin-pad-project-links-saved'

type CachedLink = {
  record: ProjectLinkRecord
  updatedAt: string
}

const recordCache = new Map<string, CachedLink>()
const inflight = new Map<string, Promise<void>>()

function projectKey(value?: string) {
  return value?.trim().toLowerCase() ?? ''
}

export function uniqueProjectKeys(keys: Array<string | undefined>) {
  return [...new Set(keys.map(projectKey).filter(Boolean))]
}

function isEthAddress(value?: string) {
  return Boolean(value && ADDRESS_RE.test(value))
}

function sameAddress(a?: string, b?: string) {
  if (!a || !b)
    return false
  return a.trim().toLowerCase() === b.trim().toLowerCase()
}

function mergeLinkRecords(base?: ProjectLinkRecord, overlay?: ProjectLinkRecord): ProjectLinkRecord {
  const socials = optionalTokenSocials({
    ...pickTokenSocials(base?.socials),
    ...pickTokenSocials(overlay?.socials),
  })
  return {
    website: overlay?.website || base?.website,
    image: overlay?.image || base?.image,
    banner: overlay?.banner || base?.banner,
    about: overlay?.about || base?.about,
    socials,
  }
}

function recordFromRow(row?: {
  website?: string
  image?: string
  banner?: string
  about?: string
  x?: string
  youtube?: string
  facebook?: string
  instagram?: string
  tiktok?: string
  telegram?: string
  discord?: string
} | null): ProjectLinkRecord {
  if (!row)
    return {}
  return {
    website: trimOptionalUrl(row.website),
    image: trimOptionalUrl(row.image),
    banner: trimOptionalUrl(row.banner),
    about: sanitizeAbout(row.about),
    socials: optionalTokenSocials(row),
  }
}

export function hasLinkRecord(record?: ProjectLinkRecord) {
  return Boolean(record?.website || record?.image || record?.banner || record?.about || optionalTokenSocials(record?.socials))
}

function cachedRecordForKeys(keys: string[]): ProjectLinkRecord {
  const rows = keys
    .map(key => recordCache.get(key))
    .filter((row): row is CachedLink => Boolean(row))
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
  return rows[0]?.record ?? {}
}

async function fetchProjectLink(key: string) {
  const pending = inflight.get(key)
  if (pending) {
    await pending
    return
  }

  const request = (async () => {
    try {
      const { list } = await lumi.entities.project_links.list({
        filter: { projectKey: key },
        sort: { updatedAt: -1 },
        limit: 1,
      })
      const row = list?.[0]
      recordCache.set(key, {
        record: recordFromRow(row),
        updatedAt: String(row?.updatedAt || ''),
      })
    }
    catch (error) {
      console.error('Load project links failed:', error)
      if (!recordCache.has(key))
        recordCache.set(key, { record: {}, updatedAt: '' })
    }
    finally {
      inflight.delete(key)
    }
  })()

  inflight.set(key, request)
  await request
}

const LINK_FETCH_BATCH = 8

export async function loadProjectLinks(keys: Array<string | undefined>): Promise<ProjectLinkRecord> {
  const unique = uniqueProjectKeys(keys)
  if (!unique.length)
    return {}

  const missing = unique.filter(key => !recordCache.has(key))
  for (let i = 0; i < missing.length; i += LINK_FETCH_BATCH) {
    await Promise.all(missing.slice(i, i + LINK_FETCH_BATCH).map(key => fetchProjectLink(key)))
  }

  return cachedRecordForKeys(unique)
}

export function prefetchProjectLinks(keys: Array<string | undefined>) {
  void loadProjectLinks(keys)
}

export function getCachedProjectLinks(keys: Array<string | undefined>): ProjectLinkRecord {
  return cachedRecordForKeys(uniqueProjectKeys(keys))
}

export function seedProjectLinkCache(rows: Array<{
  projectKey?: string
  website?: string
  image?: string
  banner?: string
  about?: string
  x?: string
  youtube?: string
  facebook?: string
  instagram?: string
  tiktok?: string
  telegram?: string
  discord?: string
  updatedAt?: string
} | null | undefined>) {
  for (const row of rows) {
    const key = projectKey(row?.projectKey)
    if (!key)
      continue
    const updatedAt = String(row?.updatedAt || '')
    const existing = recordCache.get(key)
    if (existing && existing.updatedAt && existing.updatedAt.localeCompare(updatedAt) > 0)
      continue
    recordCache.set(key, {
      record: recordFromRow(row),
      updatedAt,
    })
  }
}

export function cachedSharePaths(): string[] {
  const paths: string[] = []
  const seen = new Set<string>()
  const add = (value: string) => {
    const slug = value.trim().replace(/^\/+/, '')
    if (!slug || slug.includes('/') || ADDRESS_RE.test(slug))
      return
    const path = `/${slug}`
    if (seen.has(path.toLowerCase()) && seen.has(path))
      return
    if (!seen.has(path)) {
      seen.add(path)
      paths.push(path)
    }
  }
  for (const key of recordCache.keys()) {
    add(key)
    const upper = key.toUpperCase()
    if (upper !== key)
      add(upper)
  }
  return paths
}

export async function preloadProjectLinksForShare() {
  const pageSize = 100
  const maxRows = 3000
  const rows: Array<{
    projectKey?: string
    website?: string
    image?: string
    banner?: string
    about?: string
    x?: string
    youtube?: string
    facebook?: string
    instagram?: string
    tiktok?: string
    telegram?: string
    discord?: string
    updatedAt?: string
  }> = []
  try {
    for (let skip = 0; skip < maxRows; skip += pageSize) {
      const { list } = await lumi.entities.project_links.list({
        sort: { updatedAt: -1 },
        limit: pageSize,
        skip,
      })
      if (!list?.length)
        break
      rows.push(...list)
      if (list.length < pageSize)
        break
    }
    seedProjectLinkCache(rows)
  }
  catch (error) {
    console.warn('Preload project links for share cards failed:', error)
  }
}

function rememberSavedLinks(keys: string[], record: ProjectLinkRecord) {
  const updatedAt = new Date().toISOString()
  for (const key of keys) {
    const existing = recordCache.get(key)?.record
    recordCache.set(key, { record: mergeLinkRecords(existing, record), updatedAt })
  }
}

function buildAuthMessage(wallet: string) {
  const issued = new Date().toISOString()
  const expires = new Date(Date.now() + AUTH_TTL_MS).toISOString()
  return `${AUTH_PREFIX}\n\nWallet: ${wallet}\nIssued: ${issued}\nExpires: ${expires}`
}

function getCachedAuth(wallet: string) {
  if (!cachedAuth || !sameAddress(cachedAuth.wallet, wallet))
    return null
  if (cachedAuth.expiresAt - Date.now() < 5 * 60 * 1000)
    return null
  return cachedAuth
}

async function authorizeLinkUpdate(
  wallet: string,
  signMessage?: (message: string) => Promise<string>,
  forceSign = false,
): Promise<LinkAuth> {
  if (!forceSign) {
    const cached = getCachedAuth(wallet)
    if (cached)
      return cached
  }

  const message = buildAuthMessage(wallet)
  if (!signMessage)
    throw new Error('Connect the creator wallet and confirm the signature to save links')
  const signature = (await signMessage(message)).trim()
  if (!signature)
    throw new Error('Wallet signature was rejected')

  const expiresAt = Date.parse(message.split('Expires: ')[1] || '')
  cachedAuth = {
    wallet,
    message,
    signature,
    expiresAt: Number.isFinite(expiresAt) ? expiresAt : Date.now() + AUTH_TTL_MS,
  }
  return cachedAuth
}

function invokeErrorMessage(error: unknown) {
  const record = error as { data?: { error?: string }, message?: string }
  return (record?.data?.error || record?.message || 'Could not save project links').slice(0, 180)
}

export async function saveProjectLinks(input: {
  projectKey?: string
  projectKeys?: Array<string | undefined>
  kind: ProjectLinkKind
  website?: string
  image?: string
  banner?: string
  about?: string
  socials?: TokenSocials
  creatorAddress?: string
  signMessage?: (message: string) => Promise<string>
  forceSign?: boolean
}) {
  const keys = uniqueProjectKeys([input.projectKey, ...(input.projectKeys ?? [])])
  const website = trimOptionalUrl(input.website)
  const image = trimOptionalUrl(input.image)
  const banner = trimOptionalUrl(input.banner)
  const about = trimOptionalAbout(input.about)
  const socials = pickTokenSocials(input.socials)
  const creatorAddress = input.creatorAddress?.trim()
  if (!keys.length)
    return
  if (!website && !image && !banner && !about && !optionalTokenSocials(socials))
    throw new Error('Choose a banner, image, link, or About text before saving.')
  if (!creatorAddress || !isEthAddress(creatorAddress))
    throw new Error('Connect the creator wallet to publish links')
  if (!keys.some(isEthAddress))
    throw new Error('This token is still indexing on-chain. Reopen the page once the contract address is visible, then save again.')

  const record: ProjectLinkRecord = { website, image, banner, about, socials }
  try {
    const auth = await authorizeLinkUpdate(creatorAddress, input.signMessage, input.forceSign)
    const result = await lumi.functions.invoke('saveProjectLinks', {
      method: 'POST',
      body: {
        wallet: creatorAddress,
        message: auth.message,
        signature: auth.signature,
        kind: input.kind,
        projectKeys: keys,
        website,
        image,
        banner,
        about,
        socials,
      },
    }) as { ok?: boolean, error?: string } | undefined
    if (result?.error || result?.ok === false)
      throw new Error(result?.error || 'Could not save project links')
    rememberSavedLinks(keys, record)
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(LINKS_SAVED_EVENT, {
        detail: { keys, record },
      }))
    }
    return record
  }
  catch (error) {
    console.error('Save project links failed:', error)
    throw new Error(invokeErrorMessage(error))
  }
}

function mergedLinks(seed?: ProjectLinkRecord, overlay?: ProjectLinkRecord) {
  const nextSeed = mergeLinkRecords(seed)
  return hasLinkRecord(overlay) ? mergeLinkRecords(nextSeed, overlay) : nextSeed
}

export function useProjectLinks(
  keys: Array<string | undefined>,
  seed?: ProjectLinkRecord,
  _kind: ProjectLinkKind = 'curve',
): ProjectLinkRecord {
  const unique = uniqueProjectKeys(keys)
  const lookup = unique.join('|')
  const seeded = mergeLinkRecords(seed)
  const seedKey = JSON.stringify({ website: seeded.website, image: seeded.image, banner: seeded.banner, about: seeded.about, socials: seeded.socials })
  const [links, setLinks] = useState<ProjectLinkRecord>(() => mergedLinks(seeded, cachedRecordForKeys(unique)))

  useEffect(() => {
    const uniqueKeys = lookup ? lookup.split('|') : []
    const nextSeed = mergeLinkRecords(seed)
    setLinks(mergedLinks(nextSeed, cachedRecordForKeys(uniqueKeys)))
    if (!lookup)
      return

    let cancelled = false
    const applyLoaded = (loaded: ProjectLinkRecord) => {
      if (cancelled)
        return
      setLinks(mergedLinks(nextSeed, loaded))
    }
    if (uniqueKeys.some(key => !recordCache.has(key)))
      loadProjectLinks(uniqueKeys).then(applyLoaded)

    const onSaved = (event: Event) => {
      const detail = (event as CustomEvent<{ keys?: string[], record?: ProjectLinkRecord }>).detail
      const savedKeys = uniqueProjectKeys(detail?.keys ?? [])
      if (!savedKeys.some(key => uniqueKeys.includes(key)))
        return
      setLinks(mergedLinks(nextSeed, detail?.record))
    }
    window.addEventListener(LINKS_SAVED_EVENT, onSaved)

    return () => {
      cancelled = true
      window.removeEventListener(LINKS_SAVED_EVENT, onSaved)
    }
  }, [lookup, seedKey])

  return links
}

export function useTokenImage(keys: Array<string | undefined>, fallback?: string) {
  const links = useProjectLinks(keys, fallback ? { image: fallback } : undefined)
  return links.image || fallback
}
